import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useState, useRef } from 'react';

import { openSnackbar } from 'api/snackbar';

// project imports
import {
  initDB,
  getUnsyncedCount,
  getUnsyncedCountForTherapist,
  getUnsyncedEntries,
  markEntrySynced,
  cacheChildren,
  cacheSessions,
  cacheDropdownValues,
  saveUserSession,
  getUserSession,
  clearUserSession,
  isOfflineDataAvailable,
  // Session update sync functions
  getPendingSessionUpdates,
  getPendingSessionUpdatesCount,
  getPendingSessionUpdatesForTherapist,
  markSessionUpdateSynced,
  incrementSessionUpdateAttempts,
  // Multi-therapist support
  getAllTherapistProfiles,
  getTherapistProfile,
  cacheChildrenForTherapist,
  cacheSessionsForTherapist
} from 'utils/offlineDB';
import { setOfflineContextUpdater } from 'utils/axios';
import {
  startSyncManager,
  stopSyncManager,
  setOnlineStatus as setSyncManagerOnlineStatus,
  subscribeToSync,
  getSyncQueueSummary,
  triggerSync
} from 'utils/syncManager';
import { purgeExpiredOfflineData } from 'utils/offlineRetention';

// initial state
const initialState = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isOfflineReady: false,
  unsyncedCount: 0,
  isSyncing: false,
  lastSyncTime: null,
  syncError: null,
  offlineUser: null,
  // Methods
  refreshUnsyncedCount: () => {},
  syncOfflineData: () => Promise.resolve(),
  cacheDataForOffline: () => Promise.resolve(),
  checkOfflineReady: () => Promise.resolve(false),
  setOfflineUser: () => {},
  clearOfflineSession: () => Promise.resolve(),
  updateOnlineStatus: () => {},
  checkNetworkConnectivity: () => Promise.resolve(false),
  // Sync manager methods
  triggerSync: () => Promise.resolve(),
  getSyncQueueSummary: () =>
    Promise.resolve({ entries: { pending: 0, syncing: 0, failed: 0, total: 0 }, sessions: { pending: 0, total: 0 }, total: 0 }),
  // Multi-therapist offline support
  availableOfflineUsers: [],
  activeOfflineUser: null,
  selectOfflineUser: () => Promise.resolve(false),
  clearActiveOfflineUser: () => {},
  refreshOfflineProfiles: () => Promise.resolve()
};

// ==============================|| OFFLINE CONTEXT & PROVIDER ||============================== //

const OfflineContext = createContext(initialState);

function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [isOfflineReady, setIsOfflineReady] = useState(false);
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [offlineUser, setOfflineUserState] = useState(null);
  const [dbInitialized, setDbInitialized] = useState(false);

  // Multi-therapist offline support
  const [availableOfflineUsers, setAvailableOfflineUsers] = useState([]);
  const [activeOfflineUser, setActiveOfflineUser] = useState(null);

  const syncInProgressRef = useRef(false);
  const networkCheckIntervalRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);
  const isOnlineRef = useRef(isOnline);
  const lastTransitionRef = useRef(0); // cooldown to prevent rapid toggling
  const offlineUserRef = useRef(offlineUser);

  // Keep refs in sync with state
  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    offlineUserRef.current = offlineUser;
  }, [offlineUser]);

  // Update online status - called from axios interceptor on successful requests
  // Only used to CONFIRM online status (never to set offline — that's the connectivity check's job)
  const updateOnlineStatus = useCallback((online) => {
    if (online) {
      consecutiveFailuresRef.current = 0;
      if (!isOnlineRef.current) {
        lastTransitionRef.current = Date.now();
        setIsOnline(true);
      }
    }
    // NOTE: We intentionally ignore online=false here.
    // API call failures don't mean the network is down.
    // The dedicated connectivity check handles offline detection.
  }, []); // stable - no deps, uses refs

  // Register axios updater on mount
  useEffect(() => {
    setOfflineContextUpdater(updateOnlineStatus);
    return () => {
      setOfflineContextUpdater(null);
    };
  }, [updateOnlineStatus]);

  // Periodic network connectivity check
  // Uses refs to avoid dependency on isOnline (prevents useEffect re-trigger loop)
  const checkNetworkConnectivity = useCallback(async () => {
    // First check browser's online status - if false, definitely offline
    if (!navigator.onLine) {
      if (isOnlineRef.current) {
        lastTransitionRef.current = Date.now();
        setIsOnline(false);
      }
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Use the root URL to check connectivity — always returns 200
      // (Vite serves index.html in dev, nginx serves it in production)
      const checkUrl = `/?_=${Date.now()}`;

      await fetch(checkUrl, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      consecutiveFailuresRef.current = 0;

      if (!isOnlineRef.current) {
        lastTransitionRef.current = Date.now();
        setIsOnline(true);
      }
      return true;
    } catch {
      consecutiveFailuresRef.current++;
      // Require 2 consecutive failures before going offline (with cooldown)
      const cooldownElapsed = Date.now() - lastTransitionRef.current > 5000;
      if (consecutiveFailuresRef.current >= 2 && isOnlineRef.current && cooldownElapsed) {
        lastTransitionRef.current = Date.now();
        setIsOnline(false);
      }
    }
    return false;
  }, []); // stable - no deps, uses refs

  // Initialize IndexedDB on mount
  useEffect(() => {
    const initializeDB = async () => {
      try {
        await initDB();
        setDbInitialized(true);
        // Check if offline data is available
        const ready = await isOfflineDataAvailable();
        setIsOfflineReady(ready);

        // Load cached user session
        const cachedUser = await getUserSession();
        if (cachedUser) {
          setOfflineUserState(cachedUser);
        }

        // Get initial unsynced count (will be re-scoped once user is loaded)
        const count = await getUnsyncedCount();
        setUnsyncedCount(count);

        // If we have a cached user, scope the count immediately
        if (cachedUser?.id) {
          const scopedCount = await getUnsyncedCountForTherapist(cachedUser.id);
          const sessionUpdates = await getPendingSessionUpdatesForTherapist(cachedUser.id);
          setUnsyncedCount(scopedCount + sessionUpdates.length);
        }
      } catch (error) {
        console.error('Failed to initialize IndexedDB:', error);
      }
    };

    initializeDB();
  }, []);

  // Refresh unsynced count — scoped to current therapist when possible
  const refreshUnsyncedCount = useCallback(async () => {
    if (!dbInitialized) return 0;
    try {
      const currentUserId = offlineUser?.id || activeOfflineUser?.id;
      let entriesCount, sessionUpdatesCount;

      if (currentUserId) {
        // Scoped count: only this therapist's entries
        entriesCount = await getUnsyncedCountForTherapist(currentUserId);
        const sessionUpdates = await getPendingSessionUpdatesForTherapist(currentUserId);
        sessionUpdatesCount = sessionUpdates.length;
      } else {
        // Fallback: global count (no user context yet)
        entriesCount = await getUnsyncedCount();
        sessionUpdatesCount = await getPendingSessionUpdatesCount();
      }

      const totalCount = entriesCount + sessionUpdatesCount;
      setUnsyncedCount(totalCount);
      return totalCount;
    } catch (error) {
      console.error('Failed to get unsynced count:', error);
      return 0;
    }
  }, [dbInitialized, offlineUser?.id, activeOfflineUser?.id]);

  // Retention: purge SYNCED offline entries past the configured window, at most
  // once a day per browser. Its own effect on purpose - folding it into the sync
  // manager's would have restarted the sync manager every time the signed-in
  // user changed, just to satisfy the dependency it reads.
  //
  // Never touches unsynced work, whatever the window says: see
  // utils/offlineRetention.js.
  const purgeUserId = activeOfflineUser?.id || offlineUser?.id;
  useEffect(() => {
    if (!dbInitialized || !purgeUserId) return;
    purgeExpiredOfflineData(purgeUserId).catch((err) => console.warn('[Offline] Retention purge failed:', err));
  }, [dbInitialized, purgeUserId]);

  // Initialize and manage Sync Manager
  useEffect(() => {
    if (!dbInitialized) return;

    // Start the sync manager
    startSyncManager();

    // Subscribe to sync events
    const unsubscribe = subscribeToSync((event) => {
      switch (event.type) {
        case 'sync-started':
          setIsSyncing(true);
          setSyncError(null);
          break;
        case 'sync-completed': {
          setIsSyncing(false);
          setLastSyncTime(new Date().toISOString());
          // Refresh unsynced count after sync (uses therapist-scoped count)
          refreshUnsyncedCount();

          // Show toast notification when background sync completes with results
          // Only show for therapist role — other roles (inclusionhead, supervisor, admin)
          // don't use offline features and shouldn't see sync toasters
          const totalSynced = event.results?.total?.synced || 0;
          const totalFailed = event.results?.total?.failed || 0;
          const hasAuthToken = !!localStorage.getItem('serviceToken');
          const isTherapist = offlineUserRef.current?.role === 'therapist';
          if (isTherapist && totalSynced > 0 && totalFailed === 0) {
            openSnackbar({
              open: true,
              message: `${totalSynced} offline ${totalSynced === 1 ? 'entry' : 'entries'} synced successfully`,
              variant: 'alert',
              alert: { color: 'success' }
            });
          } else if (isTherapist && totalSynced > 0 && totalFailed > 0 && hasAuthToken) {
            openSnackbar({
              open: true,
              message: `${totalSynced} ${totalSynced === 1 ? 'entry' : 'entries'} synced, ${totalFailed} failed (will retry)`,
              variant: 'alert',
              alert: { color: 'warning' }
            });
          } else if (isTherapist && totalSynced === 0 && totalFailed > 0 && hasAuthToken) {
            openSnackbar({
              open: true,
              message: `Sync failed for ${totalFailed} ${totalFailed === 1 ? 'entry' : 'entries'}. Will retry automatically.`,
              variant: 'alert',
              alert: { color: 'error' }
            });
          }
          break;
        }
        case 'sync-error':
          setIsSyncing(false);
          setSyncError(event.error);
          break;
        case 'entry-synced':
        case 'session-synced':
          // Refresh count on individual sync success (uses therapist-scoped count)
          refreshUnsyncedCount();
          break;
        default:
          break;
      }
    });

    return () => {
      stopSyncManager();
      unsubscribe();
    };
  }, [dbInitialized, refreshUnsyncedCount]);

  // Update sync manager when online status changes
  useEffect(() => {
    setSyncManagerOnlineStatus(isOnline);
  }, [isOnline]);

  // Online/Offline event listeners with verification
  // No dependency on isOnline or checkNetworkConnectivity to prevent re-trigger loops
  useEffect(() => {
    const handleOnline = async () => {
      const isActuallyOnline = await checkNetworkConnectivity();
      if (isActuallyOnline) {
        consecutiveFailuresRef.current = 0;
      }
    };

    const handleOffline = () => {
      lastTransitionRef.current = Date.now();
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic connectivity check every 30 seconds
    // Longer interval to reduce unnecessary traffic; browser events handle rapid changes
    networkCheckIntervalRef.current = setInterval(() => {
      checkNetworkConnectivity();
    }, 30000);

    // Initial connectivity check (once on mount)
    checkNetworkConnectivity();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (networkCheckIntervalRef.current) {
        clearInterval(networkCheckIntervalRef.current);
      }
    };
  }, [checkNetworkConnectivity]); // checkNetworkConnectivity is now stable (empty deps)

  // Sync offline data to server
  const syncOfflineData = useCallback(
    async (apiClient) => {
      if (!dbInitialized || syncInProgressRef.current) {
        return { success: false, synced: 0, failed: 0 };
      }

      if (!isOnline) {
        return { success: false, synced: 0, failed: 0, reason: 'offline' };
      }

      syncInProgressRef.current = true;
      setIsSyncing(true);
      setSyncError(null);

      let syncedCount = 0;
      let failedCount = 0;
      let sessionUpdatesSynced = 0;
      let sessionUpdatesFailed = 0;

      try {
        // 1. First sync session updates (start/end session)
        const pendingSessionUpdates = await getPendingSessionUpdates();
        for (const update of pendingSessionUpdates) {
          try {
            const endpoint = `/api/v1/sessions/${update.sessionId}`;
            if (apiClient) {
              await apiClient.patch(endpoint, update.data);
              await markSessionUpdateSynced(update.id);
              sessionUpdatesSynced++;
            } else {
              // If no apiClient, just mark as synced for testing
              await markSessionUpdateSynced(update.id);
              sessionUpdatesSynced++;
            }
          } catch (error) {
            console.error(`Failed to sync session update ${update.id}:`, error);
            await incrementSessionUpdateAttempts(update.id);
            sessionUpdatesFailed++;
          }
        }

        // 2. Then sync data collection entries
        const unsyncedEntries = await getUnsyncedEntries();
        for (const entry of unsyncedEntries) {
          try {
            const endpoint = getEndpointForFormType(entry.formType);
            if (endpoint && apiClient) {
              // Build proper DataCollectionCreate payload with raw_data JSONB
              const payload = {
                session_id: entry.sessionId || null,
                collected_at: entry.sessionDate || new Date().toISOString(),
                collected_by_id: entry.therapistId || null,
                raw_data: { formType: entry.formType, ...entry.data }
              };
              await apiClient.post(endpoint, payload);
              await markEntrySynced(entry.id);
              syncedCount++;
            } else {
              await markEntrySynced(entry.id);
              syncedCount++;
            }
          } catch (error) {
            console.error(`Failed to sync entry ${entry.id}:`, error);
            failedCount++;
          }
        }

        setLastSyncTime(new Date().toISOString());
        await refreshUnsyncedCount();

        const totalSynced = syncedCount + sessionUpdatesSynced;
        const totalFailed = failedCount + sessionUpdatesFailed;
        return {
          success: true,
          synced: totalSynced,
          failed: totalFailed,
          details: {
            sessionUpdates: { synced: sessionUpdatesSynced, failed: sessionUpdatesFailed },
            dataEntries: { synced: syncedCount, failed: failedCount }
          }
        };
      } catch (error) {
        console.error('Sync failed:', error);
        setSyncError(error.message);
        return { success: false, synced: syncedCount, failed: failedCount, error: error.message };
      } finally {
        setIsSyncing(false);
        syncInProgressRef.current = false;
      }
    },
    [dbInitialized, isOnline, refreshUnsyncedCount]
  );

  // Cache data for offline use
  const cacheDataForOffline = useCallback(
    async (data) => {
      if (!dbInitialized) {
        return false;
      }

      try {
        const { children, sessions, dropdowns, user } = data;

        // Scope cached children/sessions to THIS therapist so each offline
        // profile only ever sees its own calendar/children. The stores key on
        // `therapistPersonId` (see offlineDB) — using the unscoped cacheChildren/
        // cacheSessions here was the bug: they clear the whole store and write
        // rows with no therapistPersonId, so every profile fell back to the same
        // global cache. Fall back to the unscoped writers only when we somehow
        // have no person id (legacy / pre-multi-therapist data).
        const therapistPersonId = user?.person_id || user?.id || null;

        if (children && Array.isArray(children)) {
          if (therapistPersonId) {
            await cacheChildrenForTherapist(children, therapistPersonId);
          } else {
            await cacheChildren(children);
          }
        }

        if (sessions && Array.isArray(sessions)) {
          if (therapistPersonId) {
            await cacheSessionsForTherapist(sessions, therapistPersonId);
          } else {
            await cacheSessions(sessions);
          }
        }

        if (dropdowns && typeof dropdowns === 'object') {
          for (const [key, values] of Object.entries(dropdowns)) {
            await cacheDropdownValues(key, values);
          }
        }

        if (user) {
          await saveUserSession(user);
          setOfflineUserState(user);
        }

        setIsOfflineReady(true);
        return true;
      } catch (error) {
        console.error('Failed to cache data for offline:', error);
        return false;
      }
    },
    [dbInitialized]
  );

  // Check if offline mode is ready
  const checkOfflineReady = useCallback(async () => {
    if (!dbInitialized) return false;
    try {
      const ready = await isOfflineDataAvailable();
      setIsOfflineReady(ready);
      return ready;
    } catch (error) {
      console.error('Failed to check offline ready status:', error);
      return false;
    }
  }, [dbInitialized]);

  // Set offline user.
  // Always reflect the user in React state immediately so guards that rely on
  // `offlineUser` work even before IndexedDB finishes initializing. Persistence
  // is best-effort (getDB lazily initializes), so we no longer silently no-op
  // when `dbInitialized` hasn't flipped yet — that race was leaving logged-in
  // therapists without an offline session.
  const setOfflineUser = useCallback(async (user) => {
    setOfflineUserState(user);
    try {
      await saveUserSession(user);
    } catch (error) {
      console.error('Failed to save offline user:', error);
    }
  }, []);

  // Clear offline session
  const clearOfflineSession = useCallback(async () => {
    if (!dbInitialized) return;
    try {
      await clearUserSession();
      setOfflineUserState(null);
    } catch (error) {
      console.error('Failed to clear offline session:', error);
    }
  }, [dbInitialized]);

  // Load all cached therapist profiles (for offline profile picker)
  const refreshOfflineProfiles = useCallback(async () => {
    if (!dbInitialized) return;
    try {
      const profiles = await getAllTherapistProfiles();
      setAvailableOfflineUsers(profiles);
    } catch (error) {
      console.error('Failed to load therapist profiles:', error);
    }
  }, [dbInitialized]);

  // Load profiles on init and whenever DB is ready
  useEffect(() => {
    if (dbInitialized) {
      refreshOfflineProfiles();
    }
  }, [dbInitialized, refreshOfflineProfiles]);

  // Select offline user (load profile and set as active)
  const selectOfflineUser = useCallback(async (userId) => {
    try {
      const profile = await getTherapistProfile(userId);
      if (profile) {
        setActiveOfflineUser(profile);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to select offline user:', error);
      return false;
    }
  }, []);

  // Clear active offline user (go back to profile picker)
  const clearActiveOfflineUser = useCallback(() => {
    setActiveOfflineUser(null);
  }, []);

  return (
    <OfflineContext.Provider
      value={{
        isOnline,
        isOfflineReady,
        unsyncedCount,
        isSyncing,
        lastSyncTime,
        syncError,
        offlineUser,
        dbInitialized,
        refreshUnsyncedCount,
        syncOfflineData,
        cacheDataForOffline,
        checkOfflineReady,
        setOfflineUser,
        clearOfflineSession,
        updateOnlineStatus,
        checkNetworkConnectivity,
        // Sync manager functions
        triggerSync,
        getSyncQueueSummary,
        // Multi-therapist offline support
        availableOfflineUsers,
        activeOfflineUser,
        selectOfflineUser,
        clearActiveOfflineUser,
        refreshOfflineProfiles
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
}

// Helper function to get API endpoint for form type
// All form types now go through the unified data-collection endpoint with raw_data JSONB
function getEndpointForFormType(formType) {
  const allFormTypes = [
    'cold-probe',
    'multi-trial',
    'daily-mand',
    'behavior-tracking',
    'abc',
    'maintenance-probe',
    'skill-tracking',
    'behavior-interval',
    'toileting-program',
    'session-notes',
    'lesson-plan',
    'parent-training',
    'session-plan',
    'injury-report'
  ];

  if (allFormTypes.includes(formType)) {
    return '/api/v1/data-collection';
  }
  return null;
}

export { OfflineProvider, OfflineContext };

OfflineProvider.propTypes = { children: PropTypes.node };
